import { DEFAULT_BOT_MODEL, findTimeControl } from '@chess/core'
import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import { roomPath } from '../lib/router.js'
import { vibrate } from '../lib/haptics.js'
import { playSound } from '../sound/engine.js'

export interface QueueProps {
  readonly timeControlId: string
  readonly navigate: (to: string) => void
}

type Status =
  | { readonly kind: 'connecting' }
  | { readonly kind: 'waiting'; readonly count: number }
  | { readonly kind: 'failed'; readonly message: string }

/**
 * How long before we admit nobody is coming.
 *
 * This is the most important number on the page. A queue with nobody in it is
 * the normal state of a new site, and thirty seconds of a spinner is the
 * difference between "quiet tonight" and "this is broken".
 */
const BOT_OFFER_MS = 30_000

export const Queue = ({ timeControlId, navigate }: QueueProps): JSX.Element => {
  const [status, setStatus] = useState<Status>({ kind: 'connecting' })
  const [waited, setWaited] = useState(0)
  const [startingBot, setStartingBot] = useState(false)
  const socketRef = useRef<WebSocket | null>(null)

  const preset = findTimeControl(timeControlId)

  useEffect(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const socket = new WebSocket(
      `${protocol}://${window.location.host}/api/queue/${timeControlId}`,
    )
    socketRef.current = socket

    socket.addEventListener('message', (event) => {
      const message = JSON.parse(event.data as string) as
        | { type: 'waiting'; count: number }
        | { type: 'paired'; roomId: string; key: string }
        | { type: 'error'; message: string }

      if (message.type === 'waiting') setStatus({ kind: 'waiting', count: message.count })
      else if (message.type === 'error') setStatus({ kind: 'failed', message: message.message })
      else {
        playSound('gameStart')
        vibrate('end')
        navigate(roomPath(message.roomId, message.key))
      }
    })

    socket.addEventListener('error', () =>
      setStatus({ kind: 'failed', message: 'could not reach the server' }),
    )

    // Closing is what leaves the queue, so unmounting — navigating away,
    // closing the tab — removes this player with no explicit cancel.
    return () => socket.close()
  }, [timeControlId, navigate])

  useEffect(() => {
    const started = Date.now()
    const id = window.setInterval(() => setWaited(Date.now() - started), 500)
    return () => window.clearInterval(id)
  }, [])

  const playBot = useCallback((): void => {
    setStartingBot(true)
    void (async (): Promise<void> => {
      try {
        socketRef.current?.close()
        const response = await fetch('/api/rooms', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            timeControl: '10+0',
            botModel: DEFAULT_BOT_MODEL,
            tzOffsetMinutes: new Date().getTimezoneOffset(),
          }),
        })
        if (!response.ok) throw new Error('no')
        const created = (await response.json()) as { roomId: string; whiteKey: string }
        navigate(roomPath(created.roomId, created.whiteKey))
      } catch {
        setStatus({ kind: 'failed', message: 'could not start a game' })
        setStartingBot(false)
      }
    })()
  }, [navigate])

  const offerBot = waited >= BOT_OFFER_MS

  return (
    <main className="mx-auto flex min-h-full w-full max-w-md flex-col items-center justify-center px-4 py-8 text-center safe-top safe-bottom">
      <div className="flex gap-1.5" aria-hidden="true">
        {[0, 1, 2].map((index) => (
          <span
            key={index}
            className="thinking-dot h-2.5 w-2.5 rounded-full bg-accent"
            style={{ animationDelay: `${index * 160}ms` }}
          />
        ))}
      </div>

      <h1 className="mt-5 text-2xl font-bold" role="status">
        {status.kind === 'failed' ? 'Something went wrong' : 'Looking for an opponent'}
      </h1>

      <p className="mt-1.5 text-sm text-ink-dim">
        {status.kind === 'failed'
          ? status.message
          : `${preset?.label ?? timeControlId} · waiting ${Math.floor(waited / 1_000)}s`}
      </p>

      {status.kind === 'waiting' && status.count > 1 && (
        <p className="mt-1 text-sm text-ink-dim">{status.count} people in the queue</p>
      )}

      {offerBot && status.kind !== 'failed' && (
        <div className="card-in mt-8 w-full rounded-panel bg-panel p-4">
          <p className="text-sm text-ink">Nobody around right now.</p>
          <button
            type="button"
            disabled={startingBot}
            onClick={playBot}
            className="mt-3 w-full rounded bg-accent px-4 py-3 text-base font-bold text-black hover:bg-accent-strong disabled:opacity-50"
          >
            {startingBot ? 'Starting…' : 'Play the computer instead'}
          </button>
          <p className="mt-2 text-xs text-ink-dim">
            Or keep waiting — you stay in the queue until you leave this page.
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate('/')}
        className="mt-6 rounded px-4 py-2 text-sm font-semibold text-ink-dim hover:text-ink"
      >
        Cancel
      </button>
    </main>
  )
}
